import { useCallback } from "react"
import { useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Link,
  useLeaveGuard,
  useLeaveGuarded,
  useNavigate,
} from "@/lib/page-rendering"

// Reports the enclosing react-hook-form's unsaved changes to the entry's
// leave guard, so the chrome's links ask before leaving.
export function RHFDirtyDraftRegistration() {
  const { isDirty } = useFormState()
  useLeaveGuard(isDirty)

  return null
}

// The discard confirmation a guarded navigation opens instead of leaving.
function DiscardDraftDialog({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const discard = useCallback(() => navigate({ href }), [navigate, href])

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("dirty_draft.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("dirty_draft.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("dirty_draft.keep_editing")}</AlertDialogCancel>
          <AlertDialogAction onClick={discard}>
            {t("dirty_draft.discard")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// A link that leaves the page freely while the entry is clean and asks to
// discard the draft once a form has reported it dirty, so shared chrome can
// render it on every page.
export function DirtyDraftLink({
  href,
  className,
  children,
  "data-testid": testId,
}: {
  href: string
  className?: string
  children: React.ReactNode
  "data-testid"?: string
}) {
  const dirty = useLeaveGuarded()

  if (!dirty) {
    return (
      <Link href={href} className={className} data-testid={testId}>
        {children}
      </Link>
    )
  }

  return (
    <DiscardDraftDialog href={href}>
      <button type="button" className={className} data-testid={testId}>
        {children}
      </button>
    </DiscardDraftDialog>
  )
}
